/* eslint-disable @atlassian/relay/graphql-naming */
/* eslint-disable jira/filename-case */
/** @jsx jsx */
import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
	useRef,
	useLayoutEffect,
	forwardRef,
	memo,
	type ReactElement,
	type MouseEventHandler,
	Fragment,
	type PropsWithChildren,
} from 'react';
// oxlint-disable-next-line no-unused-vars
import { css, keyframes, jsx } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import type { Row_issueEdge$key } from '@atlassian/jira-relay/src/__generated__/Row_issueEdge.graphql';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useInlineCreateHandlers,
	type TriggerPropsType,
	type HandlerArgs,
} from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import {
	usePrevious,
	usePreviousWithInitial,
} from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { Row_fieldSets$key } from '@atlassian/jira-relay/src/__generated__/Row_fieldSets.graphql';
import type { RowInner_fieldSets$key } from '@atlassian/jira-relay/src/__generated__/RowInner_fieldSets.graphql';
import type { RowMaybeWithDragAndDrop_fieldSets$key } from '@atlassian/jira-relay/src/__generated__/RowMaybeWithDragAndDrop_fieldSets.graphql';
import type { RowMaybeWithDragAndDrop_issueEdge$key } from '@atlassian/jira-relay/src/__generated__/RowMaybeWithDragAndDrop_issueEdge.graphql';
import type { RowWithDragAndDrop_fieldSets$key } from '@atlassian/jira-relay/src/__generated__/RowWithDragAndDrop_fieldSets.graphql';
import type { RowWithDragAndDrop_issueEdge$key } from '@atlassian/jira-relay/src/__generated__/RowWithDragAndDrop_issueEdge.graphql';
import type { RowWithSiblingCreate_fieldSets$key } from '@atlassian/jira-relay/src/__generated__/RowWithSiblingCreate_fieldSets.graphql';
import type { RowWithSiblingCreate_project$key } from '@atlassian/jira-relay/src/__generated__/RowWithSiblingCreate_project.graphql.ts';
import type { RowInner_issueEdge$key } from '@atlassian/jira-relay/src/__generated__/RowInner_issueEdge.graphql';
import type { RowWithSiblingCreate_issueEdge$key } from '@atlassian/jira-relay/src/__generated__/RowWithSiblingCreate_issueEdge.graphql';
import type { RowInner_project$key } from '@atlassian/jira-relay/src/__generated__/RowInner_project.graphql.ts';
import type { RowWithDragAndDrop_project$key } from '@atlassian/jira-relay/src/__generated__/RowWithDragAndDrop_project.graphql.ts';
import type { RowMaybeWithDragAndDrop_project$key } from '@atlassian/jira-relay/src/__generated__/RowMaybeWithDragAndDrop_project.graphql.ts';
import {
	getItemKey,
	useFlatList,
	useHierarchyActions,
	useIsItemExpanded,
	useIsItemVisuallyExpanded,
	useIsSelected,
	useIsFocused,
	type ItemDetails,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import { getFlatListKey } from '@atlassian/jira-issue-table-hierarchy/src/common/utils/get-flat-list-key/index.tsx';
import type { Row_project$key } from '@atlassian/jira-relay/src/__generated__/Row_project.graphql';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { RowInner_rules$key } from '@atlassian/jira-relay/src/__generated__/RowInner_rules.graphql';
import type { Row_rules$key } from '@atlassian/jira-relay/src/__generated__/Row_rules.graphql';
import type { RowWithSiblingCreate_rules$key } from '@atlassian/jira-relay/src/__generated__/RowWithSiblingCreate_rules.graphql';
import type { RowWithDragAndDrop_rules$key } from '@atlassian/jira-relay/src/__generated__/RowWithDragAndDrop_rules.graphql';
import type { RowMaybeWithDragAndDrop_rules$key } from '@atlassian/jira-relay/src/__generated__/RowMaybeWithDragAndDrop_rules.graphql';
import { useIsHydrateRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import type { RowHeight } from '../../common/types.tsx';
import { useDraggableRow } from '../../controllers/draggable-rows/index.tsx';
import { useScrollStateSelector } from '../../controllers/scroll-state/index.tsx';
import {
	useIsIssueHierarchyEnabled,
	useIsEntireRowDraggable,
	useIsSiblingIssueCreateEnabled,
	useProjectContext,
	useIsColumnBordersEnabled,
	useIsDragAndDropEnabled,
	useIsReparentingEnabled,
	useIsGridNavigationEnabled,
	useIsConditionalFormattingEnabled,
	useCellBorderStyle,
	useIsTimelineEnabled,
} from '../../controllers/features/selectors.tsx';
import {
	useIsIssueSelected,
	useIsIssueSelectedOld,
} from '../../controllers/selected-issues/index.tsx';
import {
	useSiblingIssueCreateHandler,
	SiblingIssueCreateRowContext,
} from '../../controllers/sibling-issue-create-handler/index.tsx';
import { useObserveItem } from '../../controllers/table-items-intersection-observer/index.tsx';
import { useRowContext } from '../rows/row-context/index.tsx';
import { BeforeFirstCell } from './before-first-cell/BeforeFirstCell.tsx';
import BlankIssueRow from './blank-row/index.tsx';
import { IssueCreateRow } from './issue-create-row/index.tsx';
import RemoteIssueErrorRow from './remote-issue-error-row/index.tsx';
import { IssueRow } from './issue-row/IssueRow.tsx';
import type { RowProps } from './types.tsx';
import { useRowColor } from './hooks/useRowColor.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-row" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

type RowInnerProps = Omit<RowProps, 'issueEdge' | 'fieldSets' | 'project' | 'formattingRules'> & {
	project?: RowInner_project$key | null;
	issueEdge: RowInner_issueEdge$key | null;
	fieldSets: RowInner_fieldSets$key | null;
	formattingRules?: RowInner_rules$key | null;
	onMouseLeave?: MouseEventHandler;
	onMouseMove?: MouseEventHandler;
	beforeRow?: ReactElement;
	createChildTriggerProps?: TriggerPropsType;
	isCreateChildFormVisible?: boolean;
	projectKey?: string | undefined | null;
	isGrouping?: boolean;
	isStickyColumnsVisible?: boolean;
};

const RowWithSiblingCreate = (
	props: Omit<RowProps, 'issueEdge' | 'fieldSets' | 'project' | 'formattingRules'> & {
		project?: RowWithSiblingCreate_project$key | null;
		issueEdge: RowWithSiblingCreate_issueEdge$key | null;
		fieldSets: RowWithSiblingCreate_fieldSets$key | null;
		formattingRules?: RowWithSiblingCreate_rules$key | null;
	},
) => {
	const {
		itemsConnectionId,
		rowIndex,
		parentIssueAri,
		rankFieldId,
		parentIssueHierarchyLevel,
		groupContextFieldInput,
		rowInlineCreateCanShowAdditionalFields,
		topLevelIssueCreateConfig,
	} = props;

	const projectRef = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowWithSiblingCreate_project on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				...RowMaybeWithDragAndDrop_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		props.project,
	);

	const formattingRulesRef = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowWithSiblingCreate_rules on JiraFormattingRuleConnection {
				...RowMaybeWithDragAndDrop_rules
			}
		`,
		props.formattingRules,
	);

	const issueRowEdgeData = useFragment<RowWithSiblingCreate_issueEdge$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowWithSiblingCreate_issueEdge on JiraIssueEdge
			@argumentDefinitions(
				isReparentingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
			) {
				...RowMaybeWithDragAndDrop_issueEdge
					@arguments(
						isReparentingEnabled: $isReparentingEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				node {
					id
					issueId
				}
			}
		`,
		props.issueEdge,
	);

	const issueRowData = issueRowEdgeData?.node;

	const fieldSetsData = useFragment<RowWithSiblingCreate_fieldSets$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowWithSiblingCreate_fieldSets on JiraIssueFieldSetConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
			) {
				...RowMaybeWithDragAndDrop_fieldSets
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isDensityFull: $isDensityFull
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
			}
		`,
		props.fieldSets,
	);

	const { onMouseLeave, onMouseMove } = useSiblingIssueCreateHandler(itemsConnectionId, rowIndex);

	const projectContext = useProjectContext();

	let inlineCreateHandlersInput: HandlerArgs;
	if (parentIssueHierarchyLevel != null && fg('jsc_list_reranking_gate')) {
		inlineCreateHandlersInput = {
			projectKey: projectContext,
			minHierarchyLevel: parentIssueHierarchyLevel - 1,
			maxHierarchyLevel: parentIssueHierarchyLevel - 1,
		};
	} else if (parentIssueHierarchyLevel == null && fg('timeline_harmonisation_m3_targeting')) {
		inlineCreateHandlersInput = {
			projectKey: projectContext,
			maxHierarchyLevel: topLevelIssueCreateConfig?.maxHierarchyLevel,
			minHierarchyLevel: topLevelIssueCreateConfig?.minHierarchyLevel ?? 0,
		};
	} else {
		inlineCreateHandlersInput = {
			projectKey: projectContext,
			minHierarchyLevel: 0,
		};
	}
	const { isFormVisible, formProps, triggerProps } =
		useInlineCreateHandlers(inlineCreateHandlersInput);

	const issueCreateRowContext = useMemo(
		() => ({ isFormVisible, triggerProps }),
		[isFormVisible, triggerProps],
	);

	const { groupId } = useRowContext();
	const isTimelineEnabled = useIsTimelineEnabled();

	let setIsSiblingCreateVisible: (itemDetails: ItemDetails, isVisible: boolean) => void = noop;
	if (fg('jira_timeline_harmonisation_m1_targeting')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		setIsSiblingCreateVisible = useHierarchyActions().setIsSiblingCreateVisible;
	}

	// Reset the sibling create visibility when form is closed of row is unmounted
	const prevIsFormVisible = useRef(isFormVisible);
	useEffect(() => {
		if (!fg('jira_timeline_harmonisation_m1_targeting')) {
			return;
		}

		const itemId = issueRowData?.issueId;

		if (itemId && prevIsFormVisible.current && isFormVisible === false) {
			setIsSiblingCreateVisible?.({ itemsConnectionId, itemId, groupId }, false);
		}
		prevIsFormVisible.current = isFormVisible;

		return () => {
			if (isFormVisible && itemId) {
				setIsSiblingCreateVisible?.({ itemsConnectionId, itemId, groupId }, false);
			}
		};
	}, [isFormVisible, setIsSiblingCreateVisible, itemsConnectionId, groupId, issueRowData?.issueId]);

	const issueCreateRow = (
		<IssueCreateRow
			canShowAdditionalFields={
				fg('refactor_issue_table_for_timline')
					? rowInlineCreateCanShowAdditionalFields
					: !isTimelineEnabled
			}
			depth={0}
			isGrouping={!!groupId && fg('jira_list_merged_column_gate')}
			isFormVisible={isFormVisible}
			issuesConnectionId={itemsConnectionId}
			// This will always be defined as we only render the sibling issue create button when issue data is present
			siblingIssueId={issueRowData?.id ?? ''}
			rankFieldId={rankFieldId}
			parentIssueAri={parentIssueAri}
			{...(fg('jsc_list_reranking_gate') && { location: 'siblingCreateForm' })}
			{...(fg('group_by_any_field_sibling_inline_issue_create') && {
				groupContextFieldInput,
			})}
			{...formProps}
		/>
	);

	return (
		<SiblingIssueCreateRowContext.Provider value={issueCreateRowContext}>
			<MaybeRowWithDragAndDrop
				{...props}
				project={projectRef}
				formattingRules={formattingRulesRef}
				issueEdge={issueRowEdgeData ?? null}
				fieldSets={fieldSetsData ?? null}
				onMouseLeave={onMouseLeave}
				onMouseMove={onMouseMove}
				beforeRow={issueCreateRow}
			/>
		</SiblingIssueCreateRowContext.Provider>
	);
};

const RowWithDragAndDrop = (
	props: Omit<RowInnerProps, 'issueEdge' | 'fieldSets' | 'project' | 'formattingRules'> & {
		project?: RowWithDragAndDrop_project$key | null;
		issueEdge: RowWithDragAndDrop_issueEdge$key | null;
		fieldSets: RowWithDragAndDrop_fieldSets$key | null;
		formattingRules?: RowWithDragAndDrop_rules$key | null;
	},
) => {
	const projectRef = useFragment<RowWithDragAndDrop_project$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowWithDragAndDrop_project on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				...RowInner_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		props.project,
	);

	const formattingRulesRef = useFragment<RowWithDragAndDrop_rules$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowWithDragAndDrop_rules on JiraFormattingRuleConnection {
				...RowInner_rules
			}
		`,
		props.formattingRules,
	);

	const issueRowEdgeData = useFragment<RowWithDragAndDrop_issueEdge$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowWithDragAndDrop_issueEdge on JiraIssueEdge
			@argumentDefinitions(
				isReparentingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
			) {
				...RowInner_issueEdge
					@arguments(
						isReparentingEnabled: $isReparentingEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				__id
				hasChildren(filterByProjectKeys: $projectKeys)
					@include(if: $shouldQueryHasChildren)
					@optIn(to: "JiraSpreadsheetComponent-M2")
				node {
					id
				}
			}
		`,
		props.issueEdge,
	);

	const fieldSetsData = useFragment<RowWithDragAndDrop_fieldSets$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowWithDragAndDrop_fieldSets on JiraIssueFieldSetConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
			) {
				...RowInner_fieldSets
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isDensityFull: $isDensityFull
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
			}
		`,
		props.fieldSets,
	);

	const issueRowData = issueRowEdgeData?.node;

	const draggableRef = useDraggableRow({
		edgeIndex: props.rowIndex,
		issueId: issueRowData?.id ?? '',
		hierarchyLevel: props.hierarchyLevel,
		parentIssueAri: props.parentIssueAri,
		parentIssueEdgeRelayId: props.parentItemConnectionId,
		parentIssueConnectionId: props.parentIssueRelayConnectionId,
		issueEdgeRelayId: issueRowEdgeData?.__id,
		depth: props.depth,
		itemsConnectionId: props.itemsConnectionId,
		projectKey: props.projectKey,
		hasChildren: Boolean(issueRowEdgeData?.hasChildren),
		issueKey: props.issueKey,
	});

	return (
		<MaybeMemoRowInner
			{...props}
			issueEdge={issueRowEdgeData ?? null}
			fieldSets={fieldSetsData ?? null}
			project={projectRef}
			formattingRules={formattingRulesRef}
			ref={draggableRef}
		/>
	);
};

const MaybeRowWithDragAndDrop = (
	props: Omit<RowInnerProps, 'issueEdge' | 'fieldSets' | 'project' | 'formattingRules'> & {
		issueEdge: RowMaybeWithDragAndDrop_issueEdge$key | null;
		fieldSets: RowMaybeWithDragAndDrop_fieldSets$key | null;
		project?: RowMaybeWithDragAndDrop_project$key | null | undefined;
		formattingRules?: RowMaybeWithDragAndDrop_rules$key | null | undefined;
	},
) => {
	const isDragAndDropEnabled = useIsDragAndDropEnabled();

	const projectRef = useFragment<RowMaybeWithDragAndDrop_project$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowMaybeWithDragAndDrop_project on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				...RowWithDragAndDrop_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
				...RowInner_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		props.project,
	);

	const formattingRulesRef = useFragment<RowMaybeWithDragAndDrop_rules$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowMaybeWithDragAndDrop_rules on JiraFormattingRuleConnection {
				...RowWithDragAndDrop_rules
				...RowInner_rules
			}
		`,
		props.formattingRules,
	);

	const issueRowEdgeData = useFragment<RowMaybeWithDragAndDrop_issueEdge$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowMaybeWithDragAndDrop_issueEdge on JiraIssueEdge
			@argumentDefinitions(
				isReparentingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
			) {
				...RowInner_issueEdge
					@arguments(
						isReparentingEnabled: $isReparentingEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				...RowWithDragAndDrop_issueEdge
					@arguments(
						isReparentingEnabled: $isReparentingEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				node {
					id
				}
			}
		`,
		props.issueEdge,
	);

	const fieldSetsData = useFragment<RowMaybeWithDragAndDrop_fieldSets$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowMaybeWithDragAndDrop_fieldSets on JiraIssueFieldSetConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
			) {
				...RowInner_fieldSets
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isDensityFull: $isDensityFull
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
				...RowWithDragAndDrop_fieldSets
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isDensityFull: $isDensityFull
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
			}
		`,
		props.fieldSets,
	);

	if (isDragAndDropEnabled) {
		return (
			<RowWithDragAndDrop
				{...props}
				issueEdge={issueRowEdgeData ?? null}
				fieldSets={fieldSetsData ?? null}
				project={projectRef}
				formattingRules={formattingRulesRef}
			/>
		);
	}

	return (
		<MaybeMemoRowInner
			{...props}
			project={projectRef}
			formattingRules={formattingRulesRef}
			issueEdge={issueRowEdgeData ?? null}
			fieldSets={fieldSetsData ?? null}
		/>
	);
};

const RowInner = forwardRef<HTMLTableRowElement, RowInnerProps>((props, forwardedRef) => {
	const {
		rowIndex,
		project,
		issueEdge,
		fieldSets,
		itemsConnectionId,
		depth,
		onMouseLeave,
		onMouseMove,
		beforeRow,
		triggerProps,
		isFormVisible,
		parentIssueAri,
		parentIssueRelayConnectionId,
		measureElement = noop,
		hierarchyLevel,
		issueKey,
		projectKey,
		previousItemId,
		isDraggingRowFaded,
		isStickyColumnsVisible,
		formattingRules,
	} = props;

	const tableRowRef = useRef<HTMLTableRowElement>(null);
	const { groupId } = useRowContext();
	const isGridNavigationEnabled = useIsGridNavigationEnabled();
	const isConditionalFormattingEnabled = useIsConditionalFormattingEnabled();
	const cellBorderStyle = useCellBorderStyle();

	const projectRef = useFragment<RowInner_project$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowInner_project on JiraProject
			@argumentDefinitions(
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isSavedViewConditionalFormattingEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-conditional-formatting-enabled.relayprovider"
				}
			) {
				...useRowColor_rules
					@include(if: $isConditionalFormattingEnabled)
					@skip(if: $isSavedViewConditionalFormattingEnabled)
				...IssueRow @arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		project,
	);

	const formattingRulesRef = useFragment<RowInner_rules$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowInner_rules on JiraFormattingRuleConnection
			@argumentDefinitions(
				isSavedViewConditionalFormattingEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-conditional-formatting-enabled.relayprovider"
				}
			) {
				...useRowColor_savedView_rules @include(if: $isSavedViewConditionalFormattingEnabled)
				...IssueRow_rules
			}
		`,
		formattingRules,
	);

	const issueRowEdgeData = useFragment<RowInner_issueEdge$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowInner_issueEdge on JiraIssueEdge
			@argumentDefinitions(
				isReparentingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
			) {
				...IssueRowWithFragments_issueEdge
					@arguments(
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				node {
					id
					issueId
					isHighlightedIssueRow
					...BeforeFirstCell @arguments(isReparentingEnabled: $isReparentingEnabled)
				}
				issueError @include(if: $isRemoteLinkedIssuesEnabled) {
					...remoteIssueErrorRow_nativeIssueTable
				}
			}
		`,
		issueEdge,
	);

	const issueRowData = issueRowEdgeData?.node;

	const fieldSetsData = useFragment<RowInner_fieldSets$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment RowInner_fieldSets on JiraIssueFieldSetConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
			) {
				...useRowColor @include(if: $isConditionalFormattingEnabled)
				...IssueRow_fieldSets
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isDensityFull: $isDensityFull
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
			}
		`,
		fieldSets,
	);

	const itemDetails = useMemo(
		() => (issueRowData ? { itemsConnectionId, itemId: issueRowData.issueId, groupId } : null),
		[issueRowData, itemsConnectionId, groupId],
	);

	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const list = isGridNavigationEnabled ? useFlatList() : [];

	const [isSelected, { setSelectedRow }] = useIsSelected(itemDetails);
	const prevIsSelected = usePreviousWithInitial(isSelected);
	const [isFocused, { resetFocus }] = useIsFocused(itemDetails);

	const [isRowHovered, setIsRowHovered] = useState(false);
	const isDragAndDropEnabled = useIsDragAndDropEnabled();
	const isEntireRowDraggable = useIsEntireRowDraggable();
	const isReParentingEnabled = useIsReparentingEnabled();

	const [currentDraggingRow, setCurrentDraggingRow] = useState<string | null>(null);
	const isScrolling = useScrollStateSelector((scrollState) => scrollState.isScrolling);

	const isIssueSelected = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsIssueSelected({ rowIndex, issueId: issueRowData?.issueId ?? '', depth })
		: // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsIssueSelectedOld(issueRowEdgeData?.node?.issueId ?? '');

	useEffect(() => {
		// Explicitly reset our internal state to track which row is focused. We do this to prevent the DOM element being re-focused unintentionally when the component is re-rendered.
		if (isFocused) {
			resetFocus();
		}
	}, [isFocused, resetFocus]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isIssueHierarchyEnabled = useIsIssueHierarchyEnabled();

	const onMouseOverHandler = useCallback(() => {
		if (
			!isScrolling &&
			(isIssueHierarchyEnabled ||
				isConditionalFormattingEnabled ||
				isDragAndDropEnabled ||
				(isGridNavigationEnabled && fg('jira_list_merged_column_inline_create_icon_style')))
		) {
			setIsRowHovered(true);
		}
	}, [
		isScrolling,
		isIssueHierarchyEnabled,
		isConditionalFormattingEnabled,
		isDragAndDropEnabled,
		isGridNavigationEnabled,
	]);

	const onMouseLeaveHandler = useCallback(
		(e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
			onMouseLeave?.(e);
			if (
				isIssueHierarchyEnabled ||
				isConditionalFormattingEnabled ||
				isDragAndDropEnabled ||
				(isGridNavigationEnabled && fg('jira_list_merged_column_inline_create_icon_style'))
			) {
				setIsRowHovered(false);
			}
		},
		[
			onMouseLeave,
			isIssueHierarchyEnabled,
			isConditionalFormattingEnabled,
			isDragAndDropEnabled,
			isGridNavigationEnabled,
		],
	);

	const focusCurrentRow = useCallback(
		() => setSelectedRow(itemDetails),
		[setSelectedRow, itemDetails],
	);

	const onClick = useCallback(() => {
		const clickAnalytics = createAnalyticsEvent({});
		const actionSubjectId = issueRowData ? 'issueRow' : 'blankRow';
		fireUIAnalytics(clickAnalytics, 'rowItem clicked', actionSubjectId);
	}, [createAnalyticsEvent, issueRowData]);

	const isColumnBordersEnabled = useIsColumnBordersEnabled();

	let showBorderOnTopLevelItem = false;
	if (fg('jira_timeline_cell_border_style')) {
		let isExpanded = false;

		if (fg('jira_timeline_harmonisation_m1_targeting')) {
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			isExpanded = useIsItemVisuallyExpanded(itemDetails);
		} else {
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			[isExpanded] = useIsItemExpanded(itemDetails);
		}

		const previousItemDetails = previousItemId
			? {
					itemsConnectionId,
					itemId: previousItemId,
					groupId,
				}
			: null;

		let isPreviousItemExpanded = false;

		if (fg('jira_timeline_harmonisation_m1_targeting')) {
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			isPreviousItemExpanded = useIsItemVisuallyExpanded(previousItemDetails);
		} else {
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			[isPreviousItemExpanded] = useIsItemExpanded(previousItemDetails);
		}

		showBorderOnTopLevelItem =
			cellBorderStyle === 'ROW_BORDER_FOR_EXPANDED_TOP_LEVEL_ONLY' &&
			depth === 0 &&
			(isExpanded || isPreviousItemExpanded);
	}

	const isGrouping =
		!!groupId && expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false);
	const cellBorder = useMemo(
		() =>
			fg('jira_timeline_cell_border_style')
				? [
						baseCellBorderStyles,
						cellBorderLocationStyleMap[cellBorderStyle],
						showBorderOnTopLevelItem && topCellBorderStyles,
						rowIndex === 0 && !parentIssueAri && !isGrouping && firstRowCellBorderStyles,
					]
				: [
						isColumnBordersEnabled ? cellBorderStyles : cellBorderStylesWithNoColumn,
						rowIndex === 0 && !parentIssueAri && !isGrouping && firstRowCellBorderStyles,
					],
		[
			cellBorderStyle,
			showBorderOnTopLevelItem,
			rowIndex,
			parentIssueAri,
			isGrouping,
			isColumnBordersEnabled,
		],
	);

	const beforeFirstCell = fg('initialise_virtualiser_state_from_relay_store')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(() => {
				if (issueRowEdgeData?.node) {
					return (
						<BeforeFirstCell
							edgeIndex={rowIndex}
							issueId={issueRowEdgeData?.node.id}
							itemsConnectionId={itemsConnectionId}
							hierarchyLevel={hierarchyLevel}
							parentIssueAri={parentIssueAri}
							parentIssueConnectionId={parentIssueRelayConnectionId}
							issueKey={issueKey}
							projectKey={projectKey}
							{...(isDragAndDropEnabled ? { isRowHovered } : {})}
							{...(isReParentingEnabled
								? {
										issueRowData: issueRowEdgeData?.node || null,
										setCurrentDraggingRow,
									}
								: { issueRowData: null })}
						/>
					);
				}
				return null;
			}, [
				rowIndex,
				itemsConnectionId,
				hierarchyLevel,
				parentIssueAri,
				parentIssueRelayConnectionId,
				issueKey,
				projectKey,
				isDragAndDropEnabled,
				isRowHovered,
				isReParentingEnabled,
				issueRowEdgeData?.node,
			])
		: null;

	const getRowContent = () => {
		/**
		 * Fix for flicker during optimistic issue creation caused by Relay fragment race condition.
		 *
		 * When Relay replaces an optimistic issue with real server data, there's a brief moment where
		 * issueEdge.node exists but issueRowEdgeData (from useFragment) is temporarily unavailable.
		 * Returning null prevents the flicker: IssueRow → BlankIssueRow → IssueRow.
		 */
		if (
			!issueRowEdgeData?.node &&
			issueEdge &&
			'node' in issueEdge &&
			issueEdge.node &&
			fg('jira_list_fragment_data_race_condition_fix')
		) {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'issueRowEdgeDataFragmentHydration failed',
			);

			return null;
		}

		if (issueRowEdgeData?.node) {
			return (
				<IssueRow
					project={projectRef}
					issueEdge={issueRowEdgeData ?? null}
					fieldSets={fieldSetsData ?? null}
					cellBorder={cellBorder}
					isStickyColumnsVisible={isStickyColumnsVisible}
					beforeFirstCell={
						fg('initialise_virtualiser_state_from_relay_store') ? (
							beforeFirstCell
						) : (
							<BeforeFirstCell
								edgeIndex={rowIndex}
								issueId={issueRowEdgeData?.node.id}
								itemsConnectionId={itemsConnectionId}
								hierarchyLevel={hierarchyLevel}
								parentIssueAri={parentIssueAri}
								parentIssueConnectionId={parentIssueRelayConnectionId}
								issueKey={issueKey}
								projectKey={projectKey}
								{...(isDragAndDropEnabled ? { isRowHovered } : {})}
								{...(isReParentingEnabled
									? {
											issueRowData: issueRowEdgeData?.node || null,
											setCurrentDraggingRow,
										}
									: { issueRowData: null })}
							/>
						)
					}
					itemsConnectionId={itemsConnectionId}
					depth={depth}
					isRowHoveredOrSelected={isFocused || isSelected || isRowHovered}
					rowIndex={rowIndex}
					triggerProps={triggerProps}
					isFormVisible={isFormVisible}
					formattingRules={formattingRulesRef}
				/>
			);
		}
		if (issueRowEdgeData?.issueError && fg('jira-linked-work-items-targeting-gate')) {
			return (
				<RemoteIssueErrorRow
					isSelected={isSelected}
					cellBorder={cellBorder}
					errorRef={issueRowEdgeData.issueError}
					issueKey={issueKey}
				/>
			);
		}

		// Default to blank row
		return <BlankIssueRow isSelected={isSelected} cellBorder={cellBorder} />;
	};

	const rowContent = getRowContent();
	/**
	 * Only scroll table row into view when it's a _newly_ selected row.
	 *
	 * We keep track of the prev selected state to avoid jumping to the selected row on mount. This issue
	 * was introduced when we enabled virtualization as part of the infinite scroll work.
	 * @see https://getsupport.atlassian.com/browse/PCS-359806
	 */
	useLayoutEffect(() => {
		if (isSelected && isSelected !== prevIsSelected) {
			tableRowRef.current?.scrollIntoView({ block: 'nearest' });
		}
	}, [isSelected, prevIsSelected]);

	const highlightFadeOut = useRef(false);
	const onInterceptionItemRef = useObserveItem<HTMLTableRowElement>(issueRowData?.id);

	let trRef = useMergeRefs(onInterceptionItemRef, tableRowRef, forwardedRef);

	const isHighlighted = issueRowData?.isHighlightedIssueRow ?? false;
	const prevIsHighlighted = usePrevious(isHighlighted);
	// Begin fade out animation when isHighlighted changes to false
	if (prevIsHighlighted && !isHighlighted) {
		highlightFadeOut.current = true;
	}

	trRef = useMergeRefs(trRef, measureElement);

	let itemKey = null;
	if (itemsConnectionId && issueRowData?.id) {
		itemKey = getItemKey(itemsConnectionId, issueRowData.id);
	}
	const overallRowIndex =
		itemDetails && isGridNavigationEnabled ? list.indexOf(getFlatListKey(itemDetails)) + 1 : -1;

	// eslint-disable-next-line no-nested-ternary
	const color = fg('jira_list_conditional_formatting')
		? fg('saved_list_view_technical_changes')
			? // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useRowColor(fieldSetsData, null, formattingRulesRef)
			: // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useRowColor(fieldSetsData, projectRef)
		: undefined; // placeholder for what will be const color = useRowColor in the future.

	// TODO: Move inside `css` prop on `jsc-2065-unify-onrowreorder` cleanup
	const shouldHaveDraggableRowStyles = fg('jsc-2065-unify-onrowreorder')
		? isDragAndDropEnabled && isEntireRowDraggable
		: isEntireRowDraggable;

	const isPageHydrating = useIsHydrateRenderMode();

	if (!rowContent && fg('jira_list_fragment_data_race_condition_fix')) {
		return null;
	}

	return (
		<>
			{beforeRow}
			<MaybeUFOSegment>
				<tr
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="native-issue-table.ui.issue-row"
					aria-selected={(isIssueSelected && isGridNavigationEnabled) || isSelected}
					{...(overallRowIndex > 0 &&
						isGridNavigationEnabled && {
							'aria-rowindex': overallRowIndex,
						})}
					role="row"
					onAnimationEnd={() => {
						/*
						Animation set using @compiled may be inadvertently started when the scripting engine applies
						new styles (e.g. after a new issue row is appended). We need to set the animation duration
						to 0s once the animation has completed to prevent the animation restarting.
						See https://drafts.csswg.org/css-animations-1/#animations
						 */
						if (tableRowRef.current) {
							tableRowRef.current.style.animationDuration = '0s';
						}
					}}
					onFocus={focusCurrentRow}
					onClick={onClick}
					onMouseOver={onMouseOverHandler}
					onMouseMove={onMouseMove}
					// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
					onMouseLeave={onMouseLeaveHandler}
					tabIndex={-1}
					{...(isConditionalFormattingEnabled &&
						color && {
							style: { backgroundColor: isRowHovered ? color.hoverValue : color.value },
						})}
					{...{
						'data-index': rowIndex,
					}}
					ref={trRef}
					css={[
						rowStyles,
						isGridNavigationEnabled && rowStylesKeyboardNavigation,
						highlightFadeOut.current && highlightFadeOutStyles,
						// TODO This can be removed once we introduce cell-based navigation in M3 inline editing
						((isSelected && !fg('nin-grid-keyboard-navigation')) || isIssueSelected) &&
							activeRowStyles,
						isHighlighted && highlightRowStyles,
						shouldHaveDraggableRowStyles && draggableRowStyles,
						currentDraggingRow !== null &&
							currentDraggingRow === itemKey &&
							(isDraggingRowFaded || !fg('jira_timeline_harmonisation_m1_targeting')) &&
							fg('jsc_list_reparenting') &&
							draggingRowFadedStyles,
					]}
					data-vc={`issue-row${__SERVER__ && !isPageHydrating ? '-ssr' : ''}`}
					{...(__SERVER__ &&
						!isPageHydrating &&
						fg('additional_nin_ssr_placeholders') && {
							'data-ssr-placeholder': 'issue-row-placeholder',
						})}
					{...(!__SERVER__ &&
						!isPageHydrating &&
						fg('additional_nin_ssr_placeholders') && {
							'data-ssr-placeholder-replace': 'issue-row-placeholder',
						})}
				>
					{rowContent}
				</tr>
			</MaybeUFOSegment>
		</>
	);
});

const MaybeMemoRowInner = memo(RowInner);

const RowNonMemoized = (props: RowProps): JSX.Element => {
	const issueRowEdgeData = useFragment<Row_issueEdge$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment Row_issueEdge on JiraIssueEdge
			@argumentDefinitions(
				isReparentingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
			) {
				...RowInner_issueEdge
					@arguments(
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isReparentingEnabled: $isReparentingEnabled
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				...RowWithSiblingCreate_issueEdge
					@arguments(
						isReparentingEnabled: $isReparentingEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				...RowMaybeWithDragAndDrop_issueEdge
					@arguments(
						isReparentingEnabled: $isReparentingEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				...RowWithDragAndDrop_issueEdge
					@arguments(
						isReparentingEnabled: $isReparentingEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				node {
					id
				}
			}
		`,
		props.issueEdge,
	);

	const fieldSetsData = useFragment<Row_fieldSets$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment Row_fieldSets on JiraIssueFieldSetConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
			) {
				...RowInner_fieldSets
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isDensityFull: $isDensityFull
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
				...RowWithSiblingCreate_fieldSets
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isDensityFull: $isDensityFull
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
				...RowMaybeWithDragAndDrop_fieldSets
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isDensityFull: $isDensityFull
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
				...RowWithDragAndDrop_fieldSets
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isDensityFull: $isDensityFull
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
				...useRowColor @include(if: $isConditionalFormattingEnabled)
			}
		`,
		props.fieldSets,
	);

	const project = useFragment<Row_project$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment Row_project on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				...RowMaybeWithDragAndDrop_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
				...RowWithSiblingCreate_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		props.project,
	);

	const formattingRulesData = useFragment<Row_rules$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment Row_rules on JiraFormattingRuleConnection {
				...RowMaybeWithDragAndDrop_rules
				...RowWithSiblingCreate_rules
			}
		`,
		props.formattingRules,
	);

	const isSiblingIssueCreateEnabled = useIsSiblingIssueCreateEnabled();
	if (isSiblingIssueCreateEnabled) {
		return (
			<RowWithSiblingCreate
				{...props}
				issueEdge={issueRowEdgeData ?? null}
				fieldSets={fieldSetsData ?? null}
				project={project}
				formattingRules={formattingRulesData}
			/>
		);
	}

	return (
		<MaybeRowWithDragAndDrop
			{...props}
			issueEdge={issueRowEdgeData ?? null}
			fieldSets={fieldSetsData ?? null}
			project={project}
			formattingRules={formattingRulesData}
		/>
	);
};

const Row: React.FC<RowProps> = componentWithFG(
	'initialise_virtualiser_state_from_relay_store',
	memo(RowNonMemoized),
	RowNonMemoized,
);

export default Row;

const ROW_HEIGHT: RowHeight = 40;

// TODO: Remove with clean up of jira_timeline_cell_border_style
const cellBorderStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',

	borderBlockStartWidth: 'border.width',

	borderBlockEndWidth: '0',

	borderInlineStartWidth: '0',

	borderInlineEndWidth: 'border.width',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		borderInlineEndWidth: '0',
	},
});

// TODO: Remove with clean up of jira_timeline_cell_border_style
const cellBorderStylesWithNoColumn = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',

	borderBlockStartWidth: 'border.width',

	borderBlockEndWidth: '0',

	borderInlineStartWidth: '0',

	borderInlineEndWidth: '0',
});

const firstRowCellBorderStyles = xcss({
	borderBlockStartWidth: '0',
});

const baseCellBorderStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',

	borderBlockEndWidth: '0',

	borderInlineStartWidth: '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		borderInlineEndWidth: '0',
	},
});

const fullCellBorderStyles = xcss({
	borderBlockStartWidth: 'border.width',

	borderInlineEndWidth: 'border.width',
});

const noColumnCellBorderStyles = xcss({
	borderBlockStartWidth: 'border.width',

	borderInlineEndWidth: '0',
});

const noRowCellBorderStyles = xcss({
	borderBlockStartWidth: '0',

	borderInlineEndWidth: 'border.width',
});

const topCellBorderStyles = xcss({
	borderBlockStartWidth: 'border.width',
});

const cellBorderLocationStyleMap = {
	FULL_BORDER: fullCellBorderStyles,
	NO_COLUMN_BORDER: noColumnCellBorderStyles,
	ROW_BORDER_FOR_EXPANDED_TOP_LEVEL_ONLY: noRowCellBorderStyles,
};

const fadeOut = keyframes({
	from: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
	},
	to: {
		backgroundColor: token('elevation.surface'),
	},
});

const rowStyles = css({
	height: `${ROW_HEIGHT}px`,
	position: 'relative',
	'&:focus': {
		outline: 'none',
	},
	backgroundColor: token('elevation.surface'),
	'&:hover': {
		animationDuration: '0s', // Skip highlight fade out animation when the row is hovered
		backgroundColor: token('elevation.surface.hovered'),
	},
});

const rowStylesKeyboardNavigation = css({
	'&:focus-within': {
		animationDuration: '0s', // Skip highlight fade out animation when the row is selected
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
});

const draggableRowStyles = css({
	cursor: 'grab',
	'&:active': {
		cursor: 'grabbing',
	},
});

const activeRowStyles = css({
	animationDuration: '0s', // Skip highlight fade out animation when the row is selected
	backgroundColor: token('color.background.selected'),
	'&:hover': {
		backgroundColor: token('color.background.selected.hovered'),
	},
});

const highlightRowStyles = css({
	backgroundColor: token('color.background.accent.blue.subtlest'),
});

const draggingRowFadedStyles = css({
	opacity: '0.4',
});

const highlightFadeOutStyles = css({
	animationName: `${fadeOut}`,
	animationDuration: '2s',
	animationTimingFunction: 'ease-out',
});
